/* webpack-strip-block:removed */

import Debounce from 'Lib/debounce';
import Ladda from "ladda";
import "ladda/dist/ladda-themeless.min.css";

/**
 * Does a bunch of AJAX stuff all at once - returns a `Promise` which resolves `false`
 * if ajax failed, otherwise returns the array of responses received.
 *
 * Pass some AJAX mapper as `mapCallback`; every entity in the supplied `entities` array will be
 * asynchronously passed into the supplied `mapCallback`, so use this for mass POSTing etc.
 *
 * i.e:
 * ```
 * async (entity) => axios.post(/some/endpoint/${entity});
 * ```
 * @template { Object.<string, any> } T
 * @param { unknown[] } entities
 * @param { Function } mapCallback
 * @param { string } success_message
 * @param { string } failure_message
 * @param { Function } handlerCallback
 * @param { ?string } ladda HTML selector for the element to use as the Ladda loader
 *
 * @returns { Promise<Api.ParsedResponse<T>> } (`Promise` of array of responses, or `false` if exception is thrown)
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const doBulkAjax = async (entities, map_callback, success_message, failure_message, ladda) => {
  try {
    // Create ladda but only call it if it exists
    if (ladda) {
      Ladda.create(document.querySelector(`.${ladda}`)).start();
    }
    const responses = await Promise.all(entities.map(map_callback)); // do mass AJAX stuff, await all to complete...
    const successes = responses.filter(res => res.status === 200 || res.success === true); // define successes as those with status 200
    const failures = responses.filter(res => res.success === false);
    if (successes.length === 1) {
      toastr.success(`${success_message ?? 'Completed'}`);
    } else if (successes.length > 1) {
      toastr.success(`${success_message ?? 'Completed'} for ${successes.length}!`);
    }
    failures.forEach(res => toastr.error(failure_message ?? res.error?.message));
    return responses;
  } catch (err) {
    window.handleAxiosError(err);
    return false;
  } finally {
    Ladda.stopAll();
  }
};

export const awaitThenRedirect = async (endpoint, executor, ...entities) => {
  const responses = await executor(...entities);
  if (responses.every(res => res.success)) { // if all success
    const url = new URL(window.location.origin);
    url.pathname = endpoint;
    window.location.href = url.href;
  }
};

export const awaitThenBack = async (executor, ...entities) => {
  const responses = await executor(...entities);
  if (responses.every(res => res.success)) { // if all success
    window.history.back();
  }
};

/**
 * An `EditDispatcher` can be used to merge many incoming user requests
 * which require AJAX calls to the API. Internally, it uses a `Debounce`
 * object to prevent AJAX calls occurring on each new request.
 */
export class EditDispatcher {
  /** @type { Number } */
  debounce_timeout = 500;

  /** @type { String? } */
  #default_endpoint;

  /** @type { Debounce } */
  #debouncer;

  /**
   * @typedef { Object } EditRequest
   * @property { Object.<string, any> } data
   * @property { string } endpoint
   */

  /**
   * @type { Object.<string, EditRequest> }
   */
  #staged = {};

  #dispatch = async () => {
    // convert staged to [key, val] pairs (using Object.entries)
    // -> generate an ajax for each of these (return to give promise)
    // -> await all async ops to complete (unordered)
    try {
      await doBulkAjax(
        Object.entries(this.#staged),
        ([uuid, payload]) => axios[payload.method.toLowerCase()](payload.endpoint, {
          uuid,
          ...payload.data,
        }),
      );
    } catch (err) {
      this.handleAxiosError(err);
    }
    this.#staged = {}; // cleanup
  };

  /**
   * @param { Object } options
   * @param { number } options.debounce_timeout The time (in ms) the dispatcher will wait between `quickEdit` calls before firing staged changes
   * @param { string } options.default_endpoint The default endpoint used for any requests not supplied with their own
   */
  constructor(options) {
    const { debounce_timeout, default_endpoint } = {
      debounce_timeout: 500,
      default_endpoint: null,
      ...options,
    };
    this.debounce_timeout = debounce_timeout;
    this.#default_endpoint = default_endpoint;
    this.#debouncer = new Debounce(this.debounce_timeout, this.#dispatch);
  }

  get default_endpoint() {
    return this.#default_endpoint;
  }

  /**
   * Merge a change request with any prior requests (assuming dispatch didn't get to fire).
   * If the entity has no pending changes, just creates an entry for it.
   *
   * Entities passed here are 'staged'; after the specified number of milliseconds
   * (supplied in constructor defaulting to `500`), staged changes are fired together to the api.
   *
   * @param { UuidModel } entity
   * @param { string } field
   * @param { unknown } value
   * @param { { endpoint?: string, method?: string } } request_config Overrides and extra config info for ajax
   *
   * @return { void }
   */
  quickEdit(entity, field, value, request_config) {
    // make unique if arr
    if (value instanceof Array) {
      value = [...new Set(value)];
    }
    // `edit_req` = existing entry, fallback to new entry (must match `EditRequest` interface)
    const edit_req = this.#staged[entity.uuid] ?? {
      data: {},
      endpoint: request_config.endpoint ?? this.default_endpoint,
      method: request_config.method ?? `post`,
    };
    if (!edit_req.endpoint) {
      console.error(`Unable to resolve endpoint for entity ${entity.name ?? entity.uuid}`);
    } else {
      edit_req.data = { // set new 'data'...
        ...edit_req.data, // to be the current data...
        [field]: value,
      };
      this.#staged[entity.uuid] = edit_req; // stage this request
      this.#debouncer.restart(); // restart the debounce, dispatch is fired if debounce completes
    }
  }
}
